// src/pages/Dashboard.tsx
import {
  useEffect,
  useMemo,
  useState,
  FormEvent,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import { useNavigate, useLocation, useOutletContext } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, isApiError } from "../lib/api";
import type { ModeShellContext } from "../components/layout/AppShell";

// ✅ Planes (fuente única de verdad)
import {
  PlanKey,
  ResolutionKey,
  getPlanConfig,
  getMinPlanForResolution,
  costCreditsForResolution,
} from "../config/plans";

// 🔹 Solo config técnica (width/height/engine/label)
import { RESOLUTIONS_CONFIG } from "../config/qualityCost";

// ✅ A3: consumer handoff Brand -> Dashboard
import { consumeBrandPending, type DashboardStyle } from "../utils/brandHandoff";

type IAResult = {
  id: string; // uuid del backend
  prompt: string;
  style: string;
  imageUrl?: string; // signed url (expira)
  createdAt?: string;
  projectId?: string | null;
  campaignId?: string | null;

  // ✅ metadata (para que el usuario SÍ perciba diferencia)
  resolution?: ResolutionKey | null;
  engine?: string | null;
  costCredits?: number | null;
  aspectRatio?: string | null;
};

type GenerateResponse = {
  success: boolean;
  imageUrl?: string;
  generationId?: string;
  newBalance?: number;
  resolution?: string;
  engine?: string | null;
  costCredits?: number | null;
  error?: string;
};

export const ALL_RESOLUTIONS: ResolutionKey[] = ["720p", "1080p", "2k", "4k", "8k"];

const ASPECT_RATIOS: Array<{ value: string; label: string }> = [
  { value: "1:1", label: "1:1" },
  { value: "4:3", label: "4:3" },
  { value: "16:9", label: "16:9" },
  { value: "21:9", label: "21:9" },
  { value: "5:4", label: "5:4" },
  { value: "3:2", label: "3:2" },
  { value: "2:3", label: "2:3" },
  { value: "9:16", label: "9:16" },
  { value: "3:4", label: "3:4" },
  { value: "4:5", label: "4:5" },
];

function isPackKey(v: string): v is "XS" | "S" | "M" | "L" | "XL" {
  return ["XS", "S", "M", "L", "XL"].includes(v);
}

/** ✅ Proyectos (backend) */
type Project = {
  id: string;
  name: string;
  createdAt: string;
};

function revealsString(v: unknown): v is string {
  return typeof v === "string" && v.length > 0;
}

function cryptoFallbackId() {
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

// ✅ special option para dropdown
const CREATE_PROJECT_VALUE = "__create_project__";

/** ─────────────────────────────────────────────────────────────
 * ✅ Campañas (localStorage) – Pro/Premium (UI)
 * ───────────────────────────────────────────────────────────── */
type Campaign = { id: string; name: string; createdAt: string };

function cryptoId() {
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

// ✅ IMPORTANTE: esto es Creator (no brand)
function campaignsKey(userKey: string, projectId: string) {
  return `ovylon_creator_campaigns_${userKey}_${projectId}`;
}

function readCampaigns(userKey: string, projectId: string): Campaign[] {
  try {
    const raw = localStorage.getItem(campaignsKey(userKey, projectId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as any[];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((c) => ({
        id: String(c?.id || ""),
        name: String(c?.name || ""),
        createdAt: String(c?.createdAt || ""),
      }))
      .filter((c) => c.id && c.name);
  } catch {
    return [];
  }
}

function writeCampaigns(userKey: string, projectId: string, items: Campaign[]) {
  try {
    localStorage.setItem(campaignsKey(userKey, projectId), JSON.stringify(items));
  } catch {
    // ignore
  }
}

/** ─────────────────────────────────────────────────────────────
 * ✅ Helpers de UX para calidad percibida
 * ───────────────────────────────────────────────────────────── */
function getExportSizeLabel(res: ResolutionKey) {
  const cfg = (RESOLUTIONS_CONFIG as any)?.[res];
  if (cfg?.width && cfg?.height) return `${cfg.width}×${cfg.height}`;
  if (res === "720p") return "1280×720";
  if (res === "1080p") return "1920×1080";
  if (res === "2k") return "2560×1440";
  if (res === "4k") return "3840×2160";
  if (res === "8k") return "7680×4320";
  return "—";
}

function needsUpscaleToBeReal(res: ResolutionKey) {
  return res === "2k" || res === "4k" || res === "8k";
}

function prettyEngine(engine?: string | null) {
  const v = String(engine || "").toLowerCase();
  if (!v) return "—";
  if (v.includes("replicate_upscale")) return "Upscaled (Real-ESRGAN)";
  if (v.includes("replicate")) return "Replicate";
  if (v.includes("openai")) return "OpenAI";
  if (v.includes("upscale")) return "Upscaled";
  return engine || "—";
}

function normalizeResolutionKey(v?: string | null): ResolutionKey | null {
  const s = String(v || "").toLowerCase().trim();
  if (s === "720p") return "720p";
  if (s === "1080p") return "1080p";
  if (s === "2k") return "2k";
  if (s === "4k") return "4k";
  if (s === "8k") return "8k";
  return null;
}

function humanDate(iso?: string) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

/**
 * ✅ Sanitizer: nunca mostrar errores crudos como "maintenance_mode"
 */
function friendlyApiError(err?: unknown) {
  const raw = String(err || "").trim();
  if (!raw) return "Ocurrió un error. Intenta de nuevo.";

  if (raw === "maintenance_mode")
    return "El servidor está en mantenimiento. Intenta nuevamente en unos minutos.";
  if (raw === "rate_limited")
    return "Estamos recibiendo muchas solicitudes. Intenta de nuevo en unos segundos.";
  return raw;
}

/** ─────────────────────────────────────────────────────────────
 * ✅ UI tiny components (compact like Higgsfield)
 * ───────────────────────────────────────────────────────────── */
function PillLabel({ children }: { children: ReactNode }) {
  return (
    <span className="text-[10px] text-gray-400 uppercase tracking-[0.18em]">
      {children}
    </span>
  );
}

function PillSelect(props: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  title?: string;
  options: Array<{ value: string; label: string; disabled?: boolean }>;
}) {
  return (
    <select
      value={props.value}
      disabled={props.disabled}
      title={props.title}
      onChange={(e) => props.onChange(e.target.value)}
      className="
        h-10 px-3 rounded-xl text-sm
        bg-[#0c0c14] hover:bg-[#121225]
        border border-white/15 hover:border-white/25
        text-gray-100 outline-none transition
        focus:ring-2 focus:ring-indigo-500/35 focus:border-indigo-400/40
        disabled:opacity-50 disabled:cursor-not-allowed
      "
    >
      {props.options.map((o) => (
        <option
          key={o.value}
          value={o.value}
          disabled={o.disabled}
          className="bg-[#0c0c14] text-gray-100"
        >
          {o.label}
        </option>
      ))}
    </select>
  );
}

function NoisePlaceholder() {
  // pure CSS “noise-ish” look using gradients (no assets)
  return (
    <div className="relative w-full h-full overflow-hidden">
      <div
        className="
          absolute inset-0
          bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.10),transparent_35%),radial-gradient(circle_at_80%_30%,rgba(255,255,255,0.08),transparent_40%),radial-gradient(circle_at_50%_80%,rgba(255,255,255,0.06),transparent_45%),linear-gradient(135deg,rgba(99,102,241,0.18),rgba(168,85,247,0.12),rgba(15,23,42,0.35))]
          blur-[2px]
        "
      />
      <div className="absolute inset-0 bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.10),transparent)] animate-pulse opacity-40" />
      <div className="absolute inset-0 bg-black/30" />
    </div>
  );
}

export default function Dashboard() {
  const { user, logout, getAccessToken, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ define isPreview ANTES de usarlo en cualquier cálculo
  const isPreview = !user;

  // ✅ Contexto del AppShell (plan/créditos/buscador global)
  const shell = useOutletContext<ModeShellContext>();

  // ✅ Fuente única: AppShell
  const currentPlan = shell?.mePlan ?? "free";
  const credits = shell?.credits ?? null;

  // ✅ Buscador: viene del AppShell (ya NO lo manejamos aquí)
  const searchQuery = shell?.searchQuery ?? "";

  // ✅ loading de créditos (solo si NO es preview)
  const meLoading = !isPreview && credits === null;

  // ✅ Routes
  const LOGIN_PATH = "/login";

  // ─────────────────────────────────────────────
  // ✅ Navegación segura (anti-loops)
  // ─────────────────────────────────────────────
  const lastNavRef = useRef<string | null>(null);

  const safeReplace = useCallback(
    (to: string) => {
      const current = location.pathname + (location.search || "");
      if (current === to) return;
      if (lastNavRef.current === to) return;
      lastNavRef.current = to;
      navigate(to, { replace: true });
    },
    [navigate, location.pathname, location.search]
  );

  const goLogin = useCallback(
    (nextPath?: string) => {
      const next = encodeURIComponent(
        nextPath || location.pathname + (location.search || "")
      );
      safeReplace(`${LOGIN_PATH}?next=${next}`);
    },
    [LOGIN_PATH, location.pathname, location.search, safeReplace]
  );

  // ─────────────────────────────────────────────
  // ✅ Estado REAL del usuario (backend /api/me)
  // ─────────────────────────────────────────────
  const planConfig = useMemo(() => getPlanConfig(currentPlan), [currentPlan]);
  const allowedResolutions = planConfig.allowedResolutions;

  // ✅ indicador: ya cargamos /api/me al menos 1 vez
  const meReady = credits !== null;

  // ─────────────────────────────────────────────
  // ✅ Sección activa del Dashboard (se lee de ?tab=)
  // ─────────────────────────────────────────────
  const [activeSection, setActiveSection] = useState<
    "inicio" | "ia" | "proyectos" | "config"
  >(isPreview ? "ia" : "inicio");

  const setTabInUrl = useCallback(
    (tab: "inicio" | "ia" | "proyectos" | "config") => {
      const params = new URLSearchParams(location.search);
      params.set("tab", tab);
      safeReplace(`/dashboard?${params.toString()}`);
    },
    [location.search, safeReplace]
  );

  // 👉 Permite entrar directo desde Home
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");

    // ✅ Preview Mode: solo IA. Si intentan abrir otra tab por URL -> forzamos IA y normalizamos URL
    if (isPreview) {
      if (tab !== "ia") {
        setActiveSection("ia");
        const p = new URLSearchParams(location.search);
        p.set("tab", "ia");
        safeReplace(`/dashboard?${p.toString()}`);
      } else {
        setActiveSection("ia");
      }
      return;
    }

    if (tab === "ia") setActiveSection("ia");
    else if (tab === "proyectos") setActiveSection("proyectos");
    else if (tab === "config") setActiveSection("config");
    else if (tab === "inicio") setActiveSection("inicio");
    else setActiveSection("inicio");
  }, [location.search, isPreview, safeReplace]);

  // ✅ Empujar placeholder al AppShell (si existe setter)
  useEffect(() => {
    const placeholder =
      activeSection === "proyectos"
        ? "Buscar proyectos..."
        : activeSection === "ia"
        ? "Buscar prompts, estilos, 4k, upscale..."
        : "Buscar...";

    const anyShell = shell as any;
    if (anyShell && typeof anyShell.setSearchPlaceholder === "function") {
      anyShell.setSearchPlaceholder(placeholder);
    }
  }, [activeSection, shell]);

  // ─────────────────────────────────────────────
  // ✅ UI: comprar créditos
  // ─────────────────────────────────────────────
  const [buyOpen, setBuyOpen] = useState(false);
  const [buyLoading, setBuyLoading] = useState(false);
  const [buyError, setBuyError] = useState<string>("");
  const [buySuccess, setBuySuccess] = useState<string>("");

  const [packs, setPacks] = useState<
    Array<{
      packId: string;
      credits: number;
      priceUsd: number;
      name?: string | null;
      badge?: string | null;
      description?: string | null;
    }>
  >([]);
  const [packsLoading, setPacksLoading] = useState(false);
  const [packsError, setPacksError] = useState<string>("");

  // UI Generador IA
  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState<string>("Realista");
  const [aspectRatio, setAspectRatio] = useState("16:9");
  const [isGenerating, setIsGenerating] = useState(false);

  // ✅ Historial desde backend (signed URLs)
  const [results, setResults] = useState<IAResult[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");

  const [resolution, setResolution] = useState<ResolutionKey>("720p");

  // ✅ Proyectos desde backend
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState("");

  const [createProjectOpen, setCreateProjectOpen] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState<string | "none">(
    "none"
  );

  // ✅ Campañas (PRO/Premium)
  const allowCampaigns = currentPlan === "pro" || currentPlan === "premium";
  const userKey = user ? user.email || user.id : null;

  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | "none">(
    "none"
  );

  const [createCampaignOpen, setCreateCampaignOpen] = useState(false);
  const [campaignName, setCampaignName] = useState("");

  // ✅ Si vienen con ?projectId=... (desde detalle de proyecto), preseleccionar proyecto
  useEffect(() => {
    if (loading) return;
    if (!user) return;

    const params = new URLSearchParams(location.search);
    const pid = params.get("projectId");
    const tab = params.get("tab");

    if (!pid) return;

    // Esperar a que proyectos estén listos
    if (projectsLoading) return;
    if (!projects.some((p) => p.id === pid)) return;

    // Si ya está seleccionado, no hacer nada
    if (selectedProjectId === pid) return;

    setSelectedProjectId(pid);
    setSelectedCampaignId("none");

    // Si el link venía con tab=ia, lo respetamos. Si no, igual mandamos a IA.
    if (tab !== "ia") {
      setActiveSection("ia");
      setTabInUrl("ia");
    } else {
      setActiveSection("ia");
    }
  }, [
    loading,
    user,
    location.search,
    projectsLoading,
    projects,
    selectedProjectId,
    setTabInUrl,
  ]);

  // ✅ Toast premium (sin alerts)
  type ToastKind = "success" | "error" | "info" | "loading";

  const [toast, setToast] = useState<{
    open: boolean;
    kind: ToastKind;
    title: string;
    message?: string;
  }>({ open: false, kind: "info", title: "", message: "" });

  const [rateRetryIn, setRateRetryIn] = useState<number>(0);
  const rateTimerRef = useRef<number | null>(null);

  // ✅ Modal preview
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState<IAResult | null>(null);

  const openPreview = useCallback((item: IAResult) => {
    setPreviewItem(item);
    setPreviewOpen(true);
  }, []);

  const closePreview = useCallback(() => {
    setPreviewOpen(false);
    setPreviewItem(null);
  }, []);

  // ✅ “Main Preview” (center) selection
  // IMPORTANT: por defecto NO mostramos imagen (Higgsfield style).
  const [mainId, setMainId] = useState<string | null>(null);

  const mainItem = useMemo(() => {
    if (!mainId) return null;
    const found = results.find((r) => r.id === mainId);
    return found || null;
  }, [results, mainId]);

  // ✅ Higgsfield: animación de preview (blur -> sharp)
  const [mainImgLoaded, setMainImgLoaded] = useState(false);
  const lastMainUrlRef = useRef<string | null>(null);

  useEffect(() => {
    const url = mainItem?.imageUrl || null;
    if (url !== lastMainUrlRef.current) {
      lastMainUrlRef.current = url;
      setMainImgLoaded(false);
    }
  }, [mainItem?.imageUrl]);

  // ✅ Si el plan cambia y la resolución actual ya no está permitida, ajustamos
  useEffect(() => {
    if (!allowedResolutions.includes(resolution)) {
      setResolution(allowedResolutions[0] ?? "720p");
    }
  }, [allowedResolutions, resolution]);

  // ✅ Campañas: cargar desde localStorage cuando cambia project
  useEffect(() => {
    if (!userKey) return;

    if (selectedProjectId === "none") {
      setCampaigns([]);
      setSelectedCampaignId("none");
      return;
    }

    const items = readCampaigns(userKey, selectedProjectId);
    setCampaigns(items);

    if (items.length === 0) setSelectedCampaignId("none");
    else {
      const stillExists = items.some((c) => c.id === selectedCampaignId);
      if (!stillExists) setSelectedCampaignId("none");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectId, userKey]);

  const goUpgrade = useCallback(
    (feature: string, required: PlanKey) => {
      safeReplace(
        `/upgrade?feature=${encodeURIComponent(
          feature
        )}&required=${encodeURIComponent(required)}`
      );
    },
    [safeReplace]
  );

  // ✅ Cargar proyectos desde backend
  const loadProjects = useCallback(async () => {
    if (!user) return; // ✅ guard
    setProjectsError("");
    setProjectsLoading(true);

    try {
      const token = await getAccessToken();
      if (!token) throw new Error("No hay sesión activa.");

      const data = await api.projectsList(
        { sourceMode: "creator", limit: 200 },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!data?.success || !Array.isArray((data as any).projects)) {
        throw new Error(
          friendlyApiError(
            (data as any)?.error || "No se pudieron cargar los proyectos."
          )
        );
      }

      const mapped: Project[] = (data as any).projects.map((p: any) => ({
        id: p.id,
        name: p.name,
        createdAt: p.created_at,
      }));

      setProjects(mapped);
    } catch (e) {
      setProjects([]);
      setProjectsError(
        e instanceof Error ? friendlyApiError(e.message) : "Error cargando proyectos."
      );
    } finally {
      setProjectsLoading(false);
    }
  }, [getAccessToken, user]);

  // ✅ Funciones Helper
  const closeToast = useCallback(() => {
    setToast((t) => ({ ...t, open: false }));
    setRateRetryIn(0);

    if (rateTimerRef.current) {
      window.clearInterval(rateTimerRef.current);
      rateTimerRef.current = null;
    }
  }, []);

  const showToast = useCallback((kind: ToastKind, title: string, message?: string) => {
    setToast({ open: true, kind, title, message: message || "" });
  }, []);

  const startRateLimitToast = useCallback((retryAfterSec: number) => {
    const seconds = Math.max(1, Math.min(60, Number(retryAfterSec) || 10));

    if (rateTimerRef.current) {
      window.clearInterval(rateTimerRef.current);
      rateTimerRef.current = null;
    }

    setRateRetryIn(seconds);
    setToast({
      open: true,
      kind: "info",
      title: "Estamos recibiendo muchas solicitudes",
      message: `Intenta de nuevo en ${seconds}s.`,
    });

    rateTimerRef.current = window.setInterval(() => {
      setRateRetryIn((prev) => {
        const next = prev - 1;
        if (next <= 0) {
          if (rateTimerRef.current) {
            window.clearInterval(rateTimerRef.current);
            rateTimerRef.current = null;
          }

          setToast({
            open: true,
            kind: "info",
            title: "Ya puedes intentar de nuevo",
            message: "Haz click en “Generar” otra vez.",
          });

          return 0;
        }

        setToast((t) => (t.open ? { ...t, message: `Intenta de nuevo en ${next}s.` } : t));
        return next;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    return () => {
      if (rateTimerRef.current) {
        window.clearInterval(rateTimerRef.current);
        rateTimerRef.current = null;
      }
    };
  }, []);

  // ✅ Cargar historial desde backend (trae signed URLs; expiran)
  const loadGenerations = useCallback(
    async (projectId?: string | "none") => {
      if (!user) return; // ✅ guard

      setHistoryError("");
      setHistoryLoading(true);

      try {
        const token = await getAccessToken();
        if (!token) throw new Error("No hay sesión activa.");

        const pid = typeof projectId !== "undefined" ? projectId : selectedProjectId;
        const pidParam = pid && pid !== "none" ? pid : null;

        const data = await api.generationsList(
          {
            projectId: pidParam,
            sourceMode: "creator", // ✅ Dashboard SIEMPRE pide historial de Creator
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!data?.success || !Array.isArray((data as any).items)) {
          throw new Error(
            friendlyApiError((data as any)?.error || "No se pudo cargar el historial.")
          );
        }

        const mapped: IAResult[] = (data as any).items.map((g: any) => {
          const rk = normalizeResolutionKey(g.resolution);
          return {
            id: g.id,
            prompt: String(g.prompt || ""),
            style: String(g.style || "Realista"),
            imageUrl: revealsString(g.imageUrl) ? g.imageUrl : undefined,
            createdAt: g.created_at,
            projectId: g.project_id ?? null,
            campaignId: (g as any).campaign_id ?? null,
            resolution: rk,
            engine: g.engine ?? null,
            costCredits: typeof g.cost_credits === "number" ? g.cost_credits : null,
            aspectRatio: g.aspect_ratio ?? null,
          };
        });

        setResults(mapped);

        // ✅ Higgsfield behavior:
        if (mainId && !mapped.some((x) => x.id === mainId)) setMainId(null);
      } catch (e) {
        setResults([]);
        setHistoryError(
          e instanceof Error ? friendlyApiError(e.message) : "Error cargando historial."
        );
      } finally {
        setHistoryLoading(false);
      }
    },
    [getAccessToken, selectedProjectId, mainId, user]
  );

  // ✅ Primera carga (proyectos + inbox historial) cuando hay user
  useEffect(() => {
    if (loading) return;
    if (!user) return;

    loadProjects();
    loadGenerations("none"); // ✅ FIX: inbox inicial
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user]);

  // ✅ Cuando cambia el proyecto seleccionado, recargamos historial
  useEffect(() => {
    if (loading) return;
    if (!user) return;
    loadGenerations(selectedProjectId);
  }, [selectedProjectId, loadGenerations, loading, user]);

  const loadPacks = useCallback(async () => {
    setPacksError("");
    setPacksLoading(true);
    try {
      const data = await api.packs();

      if (!data?.success || !Array.isArray((data as any).packs)) {
        throw new Error(
          friendlyApiError((data as any)?.error || "No se pudieron cargar los packs.")
        );
      }

      const sorted = [...(data as any).packs].sort((a: any, b: any) => a.priceUsd - b.priceUsd);
      setPacks(sorted);
    } catch (e) {
      setPacks([]);
      setPacksError(
        e instanceof Error ? friendlyApiError(e.message) : "Error cargando packs."
      );
    } finally {
      setPacksLoading(false);
    }
  }, []);

  // ✅ Costo real por plan
  const creditsCostFor = useCallback(
    (res: ResolutionKey, count: number = 1) => {
      const perImage = costCreditsForResolution(currentPlan, res);
      return perImage * Math.max(count, 1);
    },
    [currentPlan]
  );

  const applyHandoffToUI = useCallback(
    (handoff: {
      prompt: string;
      style: DashboardStyle;
      aspectRatio: string;
      requestedResolution?: ResolutionKey;
      projectId?: string | null;
      campaignId?: string | null;
    }) => {
      setActiveSection("ia");
      setTabInUrl("ia");

      setPrompt(handoff.prompt);
      setStyle(handoff.style);
      setAspectRatio(handoff.aspectRatio);

      const desired = handoff.requestedResolution ?? "720p";
      setResolution(
        allowedResolutions.includes(desired) ? desired : allowedResolutions[0] ?? "720p"
      );

      const pid = handoff.projectId ?? null;
      setSelectedProjectId(pid ? pid : "none");

      if (!allowCampaigns) {
        setSelectedCampaignId("none");
        return;
      }

      if (!pid) {
        setSelectedCampaignId("none");
        return;
      }

      const items = userKey ? readCampaigns(userKey, pid) : [];
      setCampaigns(items);

      const cid = handoff.campaignId ?? null;
      if (allowCampaigns && cid && items.some((c) => c.id === cid)) setSelectedCampaignId(cid);
      else setSelectedCampaignId("none");
    },
    [allowedResolutions, allowCampaigns, userKey, setTabInUrl]
  );

  // ✅ Consumir handoff SOLO cuando /api/me ya cargó
  const consumedRef = useRef(false);
  useEffect(() => {
    if (loading) return;
    if (!user) return;
    if (!meReady) return;
    if (consumedRef.current) return;

    const key = user.email || user.id;
    const pending = consumeBrandPending(key);
    if (!pending) return;

    consumedRef.current = true;

    applyHandoffToUI({
      prompt: pending.prompt,
      style: pending.style,
      aspectRatio: pending.aspectRatio,
      requestedResolution: pending.requestedResolution,
      projectId: (pending as any).projectId ?? null,
      campaignId: (pending as any).campaignId ?? null,
    });
  }, [loading, user, meReady, applyHandoffToUI]);

  const handleGenerate = async (e: FormEvent) => {
    e.preventDefault();

    // ✅ Preview Mode: si no hay sesión, redirigir a login
    if (!user) {
      goLogin();
      return;
    }

    if (!prompt.trim()) return;

    if (!allowedResolutions.includes(resolution)) {
      goUpgrade(`resolution:${resolution}`, getMinPlanForResolution(resolution));
      return;
    }

    const cost = creditsCostFor(resolution, 1);

    if (credits === null) {
      showToast("info", "Cargando créditos…", "Espera un momento y vuelve a intentar.");
      return;
    }

    if (credits < cost) {
      showToast("error", "Créditos insuficientes", `Necesitas ${cost} y tienes ${credits}.`);
      return;
    }

    setIsGenerating(true);

    try {
      const token = await getAccessToken();
      if (!token) {
        goLogin();
        return;
      }

      const projectIdToSend = selectedProjectId === "none" ? null : selectedProjectId;
      const campaignIdToSend =
        allowCampaigns && selectedProjectId !== "none" && selectedCampaignId !== "none"
          ? selectedCampaignId
          : null;

      const data = await api.generate(
        {
          prompt,
          style,
          aspectRatio,
          resolution,
          projectId: projectIdToSend,
          campaignId: campaignIdToSend,
          sourceMode: "creator", // ✅ FIX: Dashboard SIEMPRE genera en Creator
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const typed = data as unknown as GenerateResponse;

      if (!typed || typed.success === false) {
        throw new Error(friendlyApiError((typed as any)?.error || "No se pudo generar la imagen."));
      }

      const imageUrlFromServer = typed.imageUrl || null;
      if (!imageUrlFromServer) throw new Error("El servidor no devolvió ninguna imagen.");

      const newItem: IAResult = {
        id: String(typed.generationId || cryptoFallbackId()),
        prompt,
        style,
        imageUrl: imageUrlFromServer,
        createdAt: new Date().toISOString(),
        projectId: projectIdToSend,
        campaignId: campaignIdToSend,

        resolution: normalizeResolutionKey(typed.resolution) ?? resolution,
        engine: typed.engine ?? null,
        costCredits: typeof typed.costCredits === "number" ? typed.costCredits : cost,
        aspectRatio: aspectRatio,
      };

      setResults((prev) => [newItem, ...prev]);

      // ✅ Después de generar, mostramos el resultado en el canvas principal
      setMainId(newItem.id);

      await loadGenerations(selectedProjectId);

      setPrompt("");
      showToast("success", "¡Listo!", "Imagen generada correctamente.");
    } catch (err) {
      console.error("Error llamando a la API:", err);

      // ✅ Upgrade (403)
      if (isApiError(err) && err.status === 403) {
        goUpgrade(`resolution:${resolution}`, getMinPlanForResolution(resolution));
        return;
      }

      // ✅ Rate limit (429) -> TOAST premium
      if (isApiError(err) && err.status === 429) {
        const retryAfter =
          typeof (err as any)?.payload?.retryAfter === "number"
            ? (err as any).payload.retryAfter
            : typeof (err as any)?.payload?.retry_after === "number"
            ? (err as any).payload.retry_after
            : typeof (err as any)?.payload?.details?.retryAfter === "number"
            ? (err as any).payload.details.retryAfter
            : typeof (err as any)?.payload?.detail?.retry_after === "number"
            ? (err as any).payload.detail.retry_after
            : 10;

        startRateLimitToast(retryAfter);
        return;
      }

      const msg = err instanceof Error ? friendlyApiError(err.message) : "Error generando imagen.";
      showToast("error", "No se pudo generar", msg);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    safeReplace(LOGIN_PATH);
  };

  const goChooseMode = useCallback(() => {
    safeReplace("/mode?choose=1");
  }, [safeReplace]);

  const handleBuyCreditsStripe = async (packId: string) => {
    setBuyError("");
    setBuySuccess("");

    try {
      setBuyLoading(true);

      const token = await getAccessToken();
      if (!token) throw new Error("Tu sesión no está activa. Inicia sesión de nuevo.");

      const key = String(packId || "").toUpperCase();
      if (!isPackKey(key)) throw new Error(`packId inválido (${packId}). Debe ser: XS | S | M | L | XL`);

      const data = await api.stripeCheckoutPack(
        { packKey: key },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!data?.success || !(data as any).url) {
        throw new Error(
          friendlyApiError((data as any)?.error || "No se pudo iniciar Checkout de Stripe.")
        );
      }

      setBuyOpen(false);
      window.location.href = (data as any).url;
    } catch (e) {
      if (isApiError(e) && e.status === 403) {
        goUpgrade("packs", "basic");
        return;
      }
      setBuyError(e instanceof Error ? friendlyApiError(e.message) : "Error iniciando checkout.");
    } finally {
      setBuyLoading(false);
    }
  };

  // ✅ Crear proyecto en backend
  const createProject = useCallback(async () => {
    const name = projectName.trim();
    if (!name) return;

    try {
      const token = await getAccessToken();
      if (!token) throw new Error("No hay sesión activa.");

      const data = await api.createProject(
        { name, sourceMode: "creator" },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!data?.success || !(data as any).project) {
        throw new Error(
          friendlyApiError((data as any)?.error || "No se pudo crear el proyecto.")
        );
      }

      const p: Project = {
        id: (data as any).project.id,
        name: (data as any).project.name,
        createdAt: (data as any).project.created_at,
      };

      setProjects((prev) => [p, ...prev]);
      setSelectedProjectId(p.id);
      setProjectName("");
      setCreateProjectOpen(false);

      setActiveSection("ia");
      setTabInUrl("ia");
      showToast("success", "Proyecto creado", "Ya puedes generar dentro de este proyecto.");
    } catch (e) {
      showToast(
        "error",
        "No se pudo crear proyecto",
        e instanceof Error ? friendlyApiError(e.message) : "Error creando proyecto."
      );
    }
  }, [projectName, getAccessToken, setTabInUrl, showToast]);

  // ✅ Crear campaña (localStorage) – PRO/Premium
  const createCampaign = useCallback(() => {
    if (!userKey) return;
    if (!allowCampaigns) return;
    if (selectedProjectId === "none") return;

    const name = campaignName.trim();
    if (!name) return;

    const newItem: Campaign = {
      id: cryptoId(),
      name,
      createdAt: new Date().toISOString(),
    };

    const next = [newItem, ...campaigns];
    setCampaigns(next);
    setSelectedCampaignId(newItem.id);
    setCampaignName("");
    setCreateCampaignOpen(false);

    writeCampaigns(userKey, selectedProjectId, next);
    showToast("success", "Campaña creada", "Organiza por semanas, formatos o ads.");
  }, [userKey, allowCampaigns, selectedProjectId, campaignName, campaigns, showToast]);

  const currentCost = creditsCostFor(resolution, 1);

  // ✅ stats / filtros
  const totalGenerations = results.length;
  const totalProjects = projects.length;

  const filteredProjects = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((p) => p.name.toLowerCase().includes(q));
  }, [projects, searchQuery]);

  const filteredResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return results;

    return results.filter((r) => {
      const inPrompt = (r.prompt || "").toLowerCase().includes(q);
      const inStyle = (r.style || "").toLowerCase().includes(q);
      const inRes = (r.resolution || "").toLowerCase().includes(q);
      const inEng = (r.engine || "").toLowerCase().includes(q);
      return inPrompt || inStyle || inRes || inEng;
    });
  }, [results, searchQuery]);

  const recentResults = useMemo(() => {
    const sorted = [...results].sort((a, b) => {
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return tb - ta;
    });
    return sorted.slice(0, 4);
  }, [results]);

  const projectCounts = useMemo(() => {
    const map: Record<string, number> = {};
    for (const r of results) {
      const pid = r.projectId;
      if (!pid) continue;
      map[pid] = (map[pid] || 0) + 1;
    }
    return map;
  }, [results]);

  const countForProject = useCallback(
    (projectId: string) => projectCounts[projectId] || 0,
    [projectCounts]
  );

  // compact options
  const projectOptions = useMemo(() => {
    const opts = [
      { value: "none", label: "Sin proyecto" },
      { value: CREATE_PROJECT_VALUE, label: "+ Crear proyecto" },
      ...projects.map((p) => ({ value: p.id, label: p.name })),
    ];
    return opts;
  }, [projects]);

  const campaignOptions = useMemo(() => {
    const opts: Array<{ value: string; label: string; disabled?: boolean }> = [
      { value: "none", label: "Sin campaña" },
    ];
    for (const c of campaigns) opts.push({ value: c.id, label: c.name });
    if (allowCampaigns && selectedProjectId !== "none")
      opts.push({ value: "__create_campaign__", label: "+ Crear campaña" });
    return opts;
  }, [campaigns, allowCampaigns, selectedProjectId]);

  return (
    <div className="w-full text-white">
      {/* hide horizontal scrollbar for compact controls */}
      <style>{`
        .no-scrollbar::-webkit-scrollbar{display:none;}
        .no-scrollbar{-ms-overflow-style:none;scrollbar-width:none;}
      `}</style>

      {/* ✅ TOAST PREMIUM */}
      {toast.open && (
        <div className="fixed z-[9999] top-4 right-4 w-[380px] max-w-[92vw]">
          <div
            className={`
              relative rounded-2xl border border-white/10 shadow-2xl overflow-hidden
              ${
                toast.kind === "error"
                  ? "bg-red-950/60"
                  : toast.kind === "success"
                  ? "bg-emerald-950/50"
                  : "bg-[#0c0c14]/80"
              }
              backdrop-blur
            `}
          >
            <div className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white">{toast.title}</div>
                  {toast.message && (
                    <div className="mt-1 text-[12px] text-gray-200/80">
                      {toast.message}
                    </div>
                  )}
                </div>

                <button
                  onClick={closeToast}
                  className="shrink-0 px-2 py-1 rounded-lg border border-white/10 hover:bg-white/10 transition text-[11px]"
                  title="Cerrar"
                >
                  ✕
                </button>
              </div>

              {rateRetryIn > 0 && (
                <div className="mt-3 h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
                  <div
                    className="h-full bg-white/60"
                    style={{
                      width: `${Math.max(
                        0,
                        Math.min(100, (rateRetryIn / 60) * 100)
                      )}%`,
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ✅ CONTENIDO (AppShell se encarga de sidebar/topbar) */}
      <div className="p-6 md:p-8">
        {/* ✅ INICIO */}
        {activeSection === "inicio" && (
          <section className="max-w-5xl">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-gray-400">
                  Creator Mode
                </div>
                <h1 className="text-2xl md:text-3xl font-bold mt-2">
                  Tu panel de creación
                </h1>
                <p className="text-gray-300/90 mt-2 text-sm md:text-base max-w-2xl leading-relaxed">
                  Aquí organizas tus proyectos y generas piezas con consistencia.
                  Empieza por crear un proyecto o genera tu primera imagen.
                </p>

                <div className="mt-3 text-[11px] text-gray-500">
                  ✅ Tip: itera en 720/1080, y cuando ya esté lista la idea, sube a
                  2K/4K/8K.
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => {
                    setActiveSection("ia");
                    setTabInUrl("ia");
                  }}
                  className="px-4 py-2 rounded-xl text-sm font-semibold
                    bg-gradient-to-r from-indigo-500 to-purple-500
                    hover:from-indigo-400 hover:to-purple-400 transition shadow-lg shadow-indigo-500/30"
                >
                  ✨ Generar imagen
                </button>

                <button
                  onClick={() => {
                    if (isPreview) {
                      goLogin("/dashboard?tab=proyectos");
                      return;
                    }
                    setActiveSection("proyectos");
                    setTabInUrl("proyectos");
                  }}
                  className="px-4 py-2 rounded-xl text-sm font-semibold border border-white/15 hover:bg-white/10 transition"
                >
                  📁 Proyectos
                </button>
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                <div className="text-xs text-gray-400">Plan</div>
                <div className="mt-1 text-lg font-semibold">{planConfig.name}</div>
                <div className="mt-2 text-[11px] text-gray-500">
                  Salida:{" "}
                  <span className="text-gray-300">
                    {allowedResolutions.join(" / ")}
                  </span>
                </div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                <div className="text-xs text-gray-400">Créditos</div>
                <div className="mt-1 text-lg font-semibold">
                  {credits === null ? (meLoading ? "…" : "—") : credits}
                </div>
                <div className="mt-2 text-[11px] text-gray-500">
                  Tip: itera en 720/1080 y exporta en 2K/4K/8K al final.
                </div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                <div className="text-xs text-gray-400">Actividad</div>
                <div className="mt-1 text-lg font-semibold">
                  {totalGenerations} creaciones
                </div>
                <div className="mt-2 text-[11px] text-gray-500">
                  {totalProjects} proyecto{totalProjects === 1 ? "" : "s"} ·
                  Organiza para repetir campañas.
                </div>
              </div>
            </div>

            <div className="mt-10">
              <div className="flex items-center justify-between gap-4">
                <div className="text-sm font-semibold text-gray-100">
                  Últimas creaciones
                </div>
                <button
                  onClick={() => {
                    setActiveSection("ia");
                    setTabInUrl("ia");
                  }}
                  className="text-[11px] px-3 py-1.5 rounded-lg border border-white/10 hover:bg-white/10 transition text-gray-200"
                >
                  Ver en Generador →
                </button>
              </div>

              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {recentResults.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => openPreview(item)}
                    className="text-left rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition overflow-hidden"
                    title={item.prompt}
                  >
                    {item.imageUrl ? (
                      <div className="w-full aspect-video overflow-hidden">
                        <img
                          src={item.imageUrl}
                          alt={item.prompt}
                          className="w-full h-full object-cover object-center"
                          draggable={false}
                          loading="lazy"
                        />
                      </div>
                    ) : (
                      <div className="w-full aspect-video bg-gradient-to-br from-indigo-800/40 via-purple-800/40 to-slate-900/60" />
                    )}

                    <div className="p-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-[11px] text-gray-400 truncate">
                          {item.style} ·{" "}
                          {item.createdAt
                            ? new Date(item.createdAt).toLocaleDateString()
                            : "—"}
                        </div>
                        <div className="text-[10px] px-2 py-1 rounded-full bg-black/30 border border-white/10 text-gray-200">
                          {(item.resolution || "—").toUpperCase()}
                        </div>
                      </div>

                      <div className="mt-1 text-xs text-gray-200 truncate">
                        {item.prompt}
                      </div>

                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <div className="text-[10px] px-2 py-1 rounded-full bg-black/30 border border-white/10 text-gray-200">
                          Export{" "}
                          {getExportSizeLabel(
                            (item.resolution as ResolutionKey) || "720p"
                          )}
                        </div>
                        <div className="text-[10px] px-2 py-1 rounded-full bg-black/30 border border-white/10 text-gray-200">
                          {prettyEngine(item.engine)}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}

                {recentResults.length === 0 && (
                  <div className="col-span-full rounded-2xl border border-white/10 bg-white/5 p-8 text-center text-gray-400">
                    <div className="text-base font-semibold text-gray-200">
                      Aún no tienes creaciones
                    </div>
                    <p className="text-sm mt-2">
                      Genera tu primera imagen para que aquí aparezca tu historial.
                    </p>
                    <div className="mt-5">
                      <button
                        onClick={() => {
                          setActiveSection("ia");
                          setTabInUrl("ia");
                        }}
                        className="px-5 py-3 bg-white text-black rounded-xl font-semibold hover:bg-gray-200 transition"
                      >
                        Ir al Generador →
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* ✅ PROYECTOS (1:1 con BrandProjects UI) */}
{activeSection === "proyectos" && (
  <section className="max-w-5xl">
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-gray-400">
          CREATOR MODE
        </div>

        <h1 className="text-2xl md:text-3xl font-bold mt-2">
          Organiza tus creaciones
        </h1>

        <p className="text-gray-300/90 mt-2 text-sm md:text-base max-w-2xl leading-relaxed">
          Crea “carpetas” por campaña, cliente o formato. Así puedes encontrar y repetir resultados en minutos.
        </p>

        {!!projectsError && (
          <div className="mt-3 text-[12px] text-rose-300">{projectsError}</div>
        )}
      </div>

      <div className="flex flex-col gap-2 items-end">
        <button
          onClick={() => setCreateProjectOpen(true)}
          className="min-w-[180px] h-10 px-4 rounded-xl text-sm font-semibold inline-flex items-center justify-center whitespace-nowrap
            bg-gradient-to-r from-indigo-500 to-purple-500
            hover:from-indigo-400 hover:to-purple-400 transition shadow-lg shadow-indigo-500/30
            disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={projectsLoading}
        >
          📁 Nuevo proyecto
        </button>

        <button
          onClick={() => {
            setActiveSection("ia");
            setTabInUrl("ia");
          }}
          className="min-w-[180px] h-10 px-4 rounded-xl text-sm font-semibold inline-flex items-center justify-center whitespace-nowrap
            border border-white/15 hover:bg-white/10 transition
            disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={projectsLoading}
        >
          ✨ Ir al generador
        </button>
      </div>
    </div>

    <div className="mt-4 flex items-center gap-2">
      <button
        onClick={loadProjects}
        className="text-[11px] px-3 py-1.5 rounded-lg border border-white/10 hover:bg-white/10 transition text-gray-200"
        title="Refrescar"
        disabled={projectsLoading}
      >
        {projectsLoading ? "⏳ Cargando..." : "🔄 Refrescar proyectos"}
      </button>

      {!!String(searchQuery || "").trim() && (
        <span className="text-[11px] text-gray-500">
          Tip: limpia “Buscar…” arriba para ver todo.
        </span>
      )}
    </div>

    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
      {filteredProjects.map((p) => {
        const itemsCount = countForProject(p.id);

        const openProjectGallery = () => {
          navigate(`/dashboard/projects/${encodeURIComponent(p.id)}`);
        };

        const openProjectGenerator = () => {
          navigate(`/dashboard?tab=ia&projectId=${encodeURIComponent(p.id)}`);
        };

        const doDeleteCreatorProject = () => {
          // ✅ UI lista. Endpoint pendiente en backend para creator:
          // DELETE /api/projects/:id (misma ruta de brand) o /api/projects/:id?sourceMode=creator
          const ok = window.confirm(
            "¿Eliminar este proyecto? Esta acción no se puede deshacer.\n\n(Nota: si aún no existe endpoint, esto solo mostrará aviso.)"
          );
          if (!ok) return;

          showToast(
            "info",
            "Eliminar proyecto (pendiente)",
            "La UI ya está lista. Falta conectar el endpoint de borrado en el backend para Creator Mode."
          );
        };

        return (
          <div
            key={p.id}
            className="rounded-2xl border border-white/10 bg-white/5 p-5 hover:bg-white/10 transition"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{p.name}</div>
                <div className="text-[11px] text-gray-500 mt-1">
                  Creado: {new Date(p.createdAt).toLocaleDateString()}
                </div>

                {/* ✅ Alineado al mismo nivel (como Brand) */}
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={openProjectGallery}
                    className="text-[11px] px-3 py-2 rounded-xl border border-white/10 hover:bg-white/10 transition text-gray-200"
                    title="Ver imágenes del proyecto"
                  >
                    🖼️ Ver
                  </button>

                  <button
                    type="button"
                    onClick={openProjectGenerator}
                    className="text-[11px] px-3 py-2 rounded-xl border border-white/10 hover:bg-white/10 transition text-gray-200"
                    title="Generar dentro de este proyecto"
                  >
                    ✨ Usar
                  </button>

                  <span className="text-[11px] px-3 py-2 rounded-xl border border-white/10 bg-black/30 text-gray-200 whitespace-nowrap">
                    {itemsCount} creación{itemsCount === 1 ? "" : "es"}
                  </span>

                  <button
                    type="button"
                    onClick={doDeleteCreatorProject}
                    className="text-[11px] px-3 py-2 rounded-xl border border-white/10 hover:bg-white/10 transition text-gray-300"
                    title="Eliminar proyecto"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {filteredProjects.length === 0 && !projectsLoading && (
        <div className="col-span-full rounded-2xl border border-white/10 bg-white/5 p-10 text-center text-gray-400">
          <div className="text-base font-semibold text-gray-200">
            Aún no tienes proyectos
          </div>
          <p className="text-sm mt-2">
            Crea uno para organizar tus campañas y repetir estilos sin caos.
          </p>

          <div className="mt-6">
            <button
              onClick={() => setCreateProjectOpen(true)}
              className="px-6 py-3 bg-white text-black rounded-xl font-semibold hover:bg-gray-200 transition"
            >
              Crear mi primer proyecto →
            </button>
          </div>
        </div>
      )}
    </div>
  </section>
)}


        {/* ✅ IA (Higgsfield-style real) */}
        {activeSection === "ia" && (
          <section className="max-w-6xl mx-auto space-y-4">
            {/* ✅ Card principal */}
            <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur-md shadow-2xl shadow-indigo-900/35">
              {/* ✅ PREVIEW (canvas) */}
              <div className="relative rounded-t-3xl overflow-hidden">
                <div className="w-full bg-black/40 h-[40vh] md:h-[52vh] max-h-[560px] min-h-[300px]">
                  {mainItem?.imageUrl ? (
                    <img
                      src={mainItem.imageUrl}
                      alt=""
                      draggable={false}
                      onLoad={() => setMainImgLoaded(true)}
                      className={[
                        "w-full h-full object-cover",
                        "transition-all duration-500 ease-out",
                        mainImgLoaded ? "opacity-100 blur-0 scale-100" : "opacity-0 blur-md scale-[1.01]",
                        isGenerating ? "opacity-70 blur-[2px]" : "",
                      ].join(" ")}
                    />
                  ) : (
                    <div className="w-full h-full relative">
                      <NoisePlaceholder />
                      {!isGenerating && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-center px-6">
                            <div className="mx-auto w-12 h-12 rounded-2xl border border-white/10 bg-black/40 backdrop-blur flex items-center justify-center text-xl">
                              ✨
                            </div>
                            <div className="mt-4 text-base font-semibold text-gray-100">
                              Tu creación aparecerá aquí
                            </div>
                            <div className="mt-2 text-sm text-gray-300/80 max-w-md mx-auto">
                              Escribe un prompt abajo y presiona{" "}
                              <span className="text-gray-100 font-semibold">Generar</span>.
                            </div>
                            {filteredResults.length > 0 && (
                              <div className="mt-3 text-[11px] text-gray-400">
                                Tip: también puedes seleccionar una miniatura en el historial.
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {isGenerating && (
                    <div className="absolute inset-0">
                      <div className="absolute inset-0 bg-black/35" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="rounded-2xl border border-white/10 bg-black/50 backdrop-blur px-5 py-3">
                          <div className="text-sm font-semibold">Generando…</div>
                          <div className="text-[11px] text-gray-300 mt-1">
                            Esto puede tardar unos segundos.
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {mainItem && (
                    <button
                      type="button"
                      onClick={() => openPreview(mainItem)}
                      className="absolute top-4 right-4 text-[11px] px-3 py-2 rounded-xl border border-white/15 bg-black/35 hover:bg-white/10 transition backdrop-blur"
                    >
                      Abrir preview ↗
                    </button>
                  )}
                </div>
              </div>

              {/* ✅ CONTROLES + PROMPT */}
              <form onSubmit={handleGenerate} className="p-4 md:p-5">
                <div className="flex items-end gap-2">
                  <div className="flex-1 grid grid-cols-5 gap-3">
                    <div className="flex flex-col gap-1">
                      <PillLabel>Proyecto</PillLabel>
                      <PillSelect
                        value={selectedProjectId}
                        onChange={(val) => {
                          if (val === CREATE_PROJECT_VALUE) {
                            if (isPreview) {
                              goLogin("/dashboard?tab=ia");
                              return;
                            }
                            setCreateProjectOpen(true);
                            return;
                          }
                          setSelectedProjectId(val as any);
                          setSelectedCampaignId("none");
                        }}
                        options={projectOptions}
                      />
                    </div>

                    <div className="flex flex-col gap-1">
                      <div className="relative h-4 flex items-center">
                        <PillLabel>Campaña</PillLabel>

                        {!allowCampaigns && (
                          <button
                            type="button"
                            onClick={() => goUpgrade("campaigns", "pro")}
                            className="
                              absolute right-0 top-1/2 -translate-y-1/2
                              text-[10px] px-2 py-0.5 rounded-lg
                              border border-indigo-400/25 bg-indigo-500/10 hover:bg-indigo-500/15
                              transition text-indigo-200 leading-none
                            "
                          >
                            Desbloquear
                          </button>
                        )}
                      </div>

                      <PillSelect
                        value={selectedCampaignId}
                        disabled={!allowCampaigns || selectedProjectId === "none"}
                        onChange={(val) => {
                          if (val === "__create_campaign__") {
                            setCreateCampaignOpen(true);
                            return;
                          }
                          setSelectedCampaignId(val as any);
                        }}
                        options={campaignOptions}
                      />
                    </div>

                    <div className="flex flex-col gap-1">
                      <PillLabel>Estilo</PillLabel>
                      <PillSelect
                        value={style}
                        onChange={setStyle}
                        options={[
                          { value: "Realista", label: "Realista" },
                          { value: "Ilustración", label: "Ilustración" },
                          { value: "Minimal", label: "Minimal" },
                          { value: "Futurista", label: "Futurista" },
                          { value: "Anime", label: "Anime" },
                          { value: "Vintage", label: "Vintage" },
                          { value: "Logo corporativo", label: "Logo corporativo" },
                          { value: "Portada épica", label: "Portada épica" },
                        ]}
                      />
                    </div>

                    <div className="flex flex-col gap-1">
                      <PillLabel>Tamaño</PillLabel>
                      <PillSelect
                        value={aspectRatio}
                        onChange={setAspectRatio}
                        options={ASPECT_RATIOS.map((a) => ({ value: a.value, label: a.label }))}
                      />
                    </div>

                    <div className="flex flex-col gap-1">
                      <PillLabel>Calidad</PillLabel>
                      <PillSelect
                        value={resolution}
                        title={`Export: ${getExportSizeLabel(resolution)}`}
                        onChange={(val) => {
                          const next = val as ResolutionKey;
                          if (!allowedResolutions.includes(next)) {
                            goUpgrade(`resolution:${next}`, getMinPlanForResolution(next));
                            return;
                          }
                          setResolution(next);
                        }}
                        options={ALL_RESOLUTIONS.map((r) => ({
                          value: r,
                          label: r.toUpperCase(),
                          disabled: !allowedResolutions.includes(r),
                        }))}
                      />
                    </div>
                  </div>

                  <div className="hidden md:block text-[11px] text-gray-500 min-w-[170px] text-right">
                    {needsUpscaleToBeReal(resolution) ? "Ideal para export final." : "Ideal para iterar rápido."}
                  </div>
                </div>

                <div className="mt-3 flex flex-col gap-2">
                  <div
                    className="
                      flex items-center gap-2
                      rounded-2xl border border-white/15 bg-black/25
                      px-3 py-2
                      hover:border-white/25 transition
                    "
                  >
                    <input
                      id="prompt"
                      name="prompt"
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      placeholder="Describe lo que quieres generar… (ej: un león en la selva, luz cinematográfica)"
                      className="
                        flex-1 h-10 px-3 rounded-xl text-sm
                        bg-[#0c0c14] hover:bg-[#121225]
                        border border-white/15 hover:border-white/25
                        text-gray-100 placeholder:text-gray-500
                        outline-none transition
                        focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-400/40
                      "
                    />

                    <button
                      type="submit"
                      disabled={
                        isGenerating ||
                        !prompt.trim() ||
                        credits === null ||
                        (credits !== null && credits < currentCost)
                      }
                      className="
                        h-10 px-5 rounded-xl text-sm font-semibold inline-flex items-center gap-2 whitespace-nowrap
                        bg-white text-black hover:bg-gray-200
                        shadow-lg shadow-black/25
                        disabled:opacity-50 disabled:cursor-not-allowed
                        transition
                        active:scale-[0.98] transition-transform
                      "
                    >
                      {isGenerating ? "Generando…" : "Generar"}
                      {!isGenerating && <span className="text-xs font-bold">→</span>}
                    </button>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 text-[11px] text-gray-400">
                    <span className="px-2 py-1 rounded-full bg-black/30 border border-white/10">
                      Coste:{" "}
                      <span className="text-gray-200 font-semibold">{currentCost}</span>{" "}
                      crédito{currentCost === 1 ? "" : "s"}
                    </span>

                    <span className="px-2 py-1 rounded-full bg-black/30 border border-white/10">
                      {resolution.toUpperCase()} · {getExportSizeLabel(resolution)}
                    </span>

                    <span className="px-2 py-1 rounded-full bg-black/30 border border-white/10">{style}</span>
                    <span className="px-2 py-1 rounded-full bg-black/30 border border-white/10">{aspectRatio}</span>

                    <span className="px-2 py-1 rounded-full bg-black/30 border border-white/10">
                      Créditos:{" "}
                      <span className="text-gray-200 font-semibold">
                        {credits === null ? "…" : credits}
                      </span>
                    </span>

                    {needsUpscaleToBeReal(resolution) && (
                      <span className="px-2 py-1 rounded-full bg-indigo-500/10 border border-indigo-400/20 text-indigo-200">
                        Export final (upscale)
                      </span>
                    )}
                  </div>
                </div>
              </form>
            </div>

            {/* HISTORIAL */}
            <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur-md p-4 md:p-5">
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm font-semibold">Historial</div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => loadGenerations(selectedProjectId)}
                    className="text-[11px] px-3 py-1.5 rounded-lg border border-white/10 hover:bg-white/10 transition text-gray-200"
                  >
                    🔄 Refrescar
                  </button>
                  <div className="text-[11px] text-gray-500">
                    {historyLoading ? "Cargando…" : `${filteredResults.length} elemento(s)`}
                  </div>
                </div>
              </div>

              {!!historyError && <div className="mt-3 text-sm text-red-300">{historyError}</div>}

              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {filteredResults.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setMainId(item.id)}
                    onDoubleClick={() => openPreview(item)}
                    className={`
                      text-left rounded-2xl border overflow-hidden transition
                      ${
                        mainId === item.id
                          ? "border-indigo-400/60 bg-indigo-500/10"
                          : "border-white/10 bg-black/25 hover:bg-white/5 hover:border-indigo-400/30"
                      }
                    `}
                    title={item.prompt}
                  >
                    <div className="relative">
                      {item.imageUrl ? (
                        <div className="w-full aspect-video overflow-hidden">
                          <img
                            src={item.imageUrl}
                            alt={item.prompt}
                            className="w-full h-full object-cover object-center"
                            draggable={false}
                            loading="lazy"
                          />
                        </div>
                      ) : (
                        <div className="aspect-video bg-gradient-to-br from-indigo-800/40 via-purple-800/40 to-slate-900/60" />
                      )}

                      <div className="absolute top-2 left-2 text-[10px] px-2 py-1 rounded-full bg-black/55 border border-white/10 text-gray-100">
                        {(item.resolution || "—").toUpperCase()}
                      </div>

                      <div className="absolute top-2 right-2 text-[10px] px-2 py-1 rounded-full bg-black/55 border border-white/10 text-gray-100">
                        {item.costCredits ? `${item.costCredits}c` : "—"}
                      </div>
                    </div>

                    <div className="p-2.5">
                      <div className="text-[11px] text-gray-200 truncate">{item.prompt}</div>
                      <div className="mt-1 text-[10px] text-gray-400 truncate">
                        {item.style} · {item.aspectRatio || "—"}
                      </div>

                      <div className="mt-2 flex items-center justify-between gap-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openPreview(item);
                          }}
                          className="text-[10px] px-2 py-1 rounded-lg border border-white/10 hover:bg-white/10 transition text-gray-200"
                        >
                          Abrir ↗
                        </button>
                        <span className="text-[10px] text-gray-500">
                          {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "—"}
                        </span>
                      </div>
                    </div>
                  </button>
                ))}

                {!historyLoading && filteredResults.length === 0 && (
                  <div className="col-span-full text-center text-gray-500 text-sm py-10">
                    <div className="text-gray-300 font-semibold">Aún no hay resultados</div>
                    <div className="mt-2 text-gray-500">Escribe un prompt arriba y presiona Generar.</div>
                    <div className="mt-5">
                      <button
                        type="button"
                        onClick={() => {
                          const el = document.getElementById("prompt") as HTMLInputElement | null;
                          el?.scrollIntoView({ behavior: "smooth", block: "center" });
                          el?.focus?.();
                        }}
                        className="text-xs px-4 py-2 rounded-xl border border-white/15 hover:bg-white/10 transition text-gray-200"
                      >
                        Escribir prompt →
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-3 text-[11px] text-gray-500">
                Tip: Click = seleccionar para preview central · Doble click o “Abrir” = preview completo.
              </div>
            </div>
          </section>
        )}

{/* ✅ CONFIG */}
{activeSection === "config" && (
  <section className="text-gray-400 text-sm space-y-6 max-w-5xl">
    <div>
      <div className="text-xs uppercase tracking-[0.25em] text-gray-400">CREATOR MODE</div>
      <h1 className="text-2xl font-bold mt-2 text-white">Configuración</h1>
    </div>

    {/* PLAN CARD */}
    <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
        {/* Left */}
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-gray-400">Plan actual</div>

          <div className="mt-3 text-lg font-semibold text-white">
            {planConfig.name} ·{" "}
            <span className="text-gray-200">créditos: {credits === null ? "…" : credits}</span>
          </div>

          <div className="mt-3 text-[12px] text-gray-400">
            Resoluciones permitidas:{" "}
            <span className="text-gray-200">{allowedResolutions.join(", ")}</span>
          </div>

          {/* Upgrade Plan (solo si no es Premium) */}
          {currentPlan !== "premium" && (
            <div className="mt-4">
              <button
                type="button"
                onClick={() => goUpgrade("plan", currentPlan === "free" ? "basic" : "pro")}
                className="text-[11px] px-3 py-2 rounded-lg
                  border border-indigo-400/30 bg-indigo-500/10
                  hover:bg-indigo-500/20 transition text-indigo-200"
              >
                🚀 Mejorar plan
              </button>
            </div>
          )}
        </div>

        {/* Right */}
        <div className="text-[12px] text-gray-400 lg:text-right">
          Recomendación: itera en 720p/1080p y finaliza en 2k/4k/8k.
        </div>
      </div>
    </div>

    {/* ACTIONS */}
    <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
      <button
        type="button"
        onClick={async () => {
          if (!user) {
            goLogin();
            return;
          }

          setBuyError("");
          setBuySuccess("");

          if (currentPlan === "free") {
            goUpgrade("packs", "basic");
            return;
          }

          setBuyOpen(true);
          if (packs.length === 0) await loadPacks();
        }}
        className="px-6 py-3 rounded-xl text-sm font-semibold
          bg-gradient-to-r from-indigo-500 to-purple-500
          hover:from-indigo-400 hover:to-purple-400
          transition shadow-lg shadow-indigo-500/30"
      >
        💳 Comprar créditos
      </button>

      <button
        type="button"
        onClick={goChooseMode}
        className="px-6 py-3 rounded-xl text-sm font-semibold
          border border-white/15 hover:bg-white/10 transition"
      >
        🔁 Cambiar modo
      </button>

      {!isPreview && (
        <button
          type="button"
          onClick={handleLogout}
          className="px-6 py-3 rounded-xl text-sm font-semibold
            border border-white/15 hover:bg-white/10 transition text-gray-300"
        >
          Cerrar sesión
        </button>
      )}
    </div>

    {/* ✅ Modal comprar créditos */}
    {buyOpen && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
        onClick={() => (buyLoading ? null : setBuyOpen(false))}
      >
        <div
          className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#0c0c14] p-6 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-white">Comprar créditos</h2>
              <p className="text-xs text-gray-400 mt-1">Packs cargados desde el backend.</p>
            </div>

            <button
              disabled={buyLoading}
              onClick={() => setBuyOpen(false)}
              className="px-3 py-1.5 rounded-lg border border-white/10 hover:bg-white/10 transition text-xs text-gray-200 disabled:opacity-50"
            >
              ✕
            </button>
          </div>

          <div className="mt-4 flex items-center justify-between">
            <button
              type="button"
              disabled={packsLoading}
              onClick={loadPacks}
              className="text-xs px-3 py-1.5 rounded-lg border border-white/10 hover:bg-white/10 transition disabled:opacity-50"
            >
              {packsLoading ? "Cargando..." : "🔄 Recargar packs"}
            </button>

            <p className="text-[11px] text-gray-500">
              Saldo actual:{" "}
              <span className="text-gray-200 font-semibold">{credits === null ? "…" : credits}</span>
            </p>
          </div>

          {packsError && <p className="mt-3 text-sm text-red-300">{packsError}</p>}

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {packsLoading && packs.length === 0 && (
              <div className="col-span-full text-sm text-gray-400">Cargando packs...</div>
            )}
            {!packsLoading && packs.length === 0 && !packsError && (
              <div className="col-span-full text-sm text-gray-400">No hay packs disponibles.</div>
            )}

            {packs.map((p) => (
              <button
                key={p.packId}
                disabled={buyLoading}
                onClick={() => handleBuyCreditsStripe(p.packId)}
                className="relative text-left p-4 rounded-2xl border border-white/10 hover:border-indigo-400/60 hover:bg-white/5 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {p.badge && (
                  <span className="absolute top-2 right-2 text-[10px] px-2 py-1 rounded-full bg-indigo-500/20 border border-indigo-400/30 text-indigo-200">
                    {p.badge}
                  </span>
                )}

                <p className="text-sm font-semibold text-white">{p.name || p.packId}</p>
                <p className="text-xs text-gray-400 mt-1">+{p.credits} créditos</p>
                <p className="text-xs text-gray-300 mt-2">USD ${p.priceUsd}</p>
                {p.description && <p className="text-[11px] text-gray-500 mt-2">{p.description}</p>}
              </button>
            ))}
          </div>

          {/* ✅ Feedback visual (arregla ESLint) */}
          {buyError && <p className="mt-4 text-sm text-red-300">{buyError}</p>}
          {buySuccess && <p className="mt-4 text-sm text-emerald-300">{buySuccess}</p>}

          <div className="mt-5 flex justify-end">
            <button
              disabled={buyLoading}
              onClick={() => setBuyOpen(false)}
              className="px-4 py-2 rounded-xl text-sm font-semibold border border-white/15 hover:bg-white/10 transition disabled:opacity-50"
            >
              {buyLoading ? "Procesando..." : "Cerrar"}
            </button>
          </div>
        </div>
      </div>
    )}
  </section>
)}

        {/* ✅ MODAL: crear proyecto */}
        {createProjectOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4"
            onClick={() => setCreateProjectOpen(false)}
          >
            <div
              className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0c0c14] p-6 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-gray-400">Nuevo proyecto</div>
                  <div className="text-lg font-bold text-white mt-2">Crea una carpeta para organizar</div>
                  <p className="text-sm text-gray-400 mt-2">
                    Ejemplos: “Campaña Proteína”, “Landing Abogados”, “UGC Fitness”, “Cliente X”.
                  </p>
                </div>

                <button
                  onClick={() => setCreateProjectOpen(false)}
                  className="px-3 py-1.5 rounded-lg border border-white/10 hover:bg-white/10 transition text-xs text-gray-200"
                >
                  ✕
                </button>
              </div>

              <div className="mt-5">
                <label className="block text-xs text-gray-400 mb-2">Nombre del proyecto</label>
                <input
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="Ej: Campaña Black Friday"
                  className="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-gray-100 outline-none focus:ring-2 focus:ring-indigo-500/40 text-sm"
                />
              </div>

              <div className="mt-5 flex justify-end gap-2">
                <button
                  onClick={() => setCreateProjectOpen(false)}
                  className="px-4 py-2 rounded-xl text-sm font-semibold border border-white/15 hover:bg-white/10 transition"
                >
                  Cancelar
                </button>
                <button
                  onClick={createProject}
                  className="px-4 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-400 hover:to-purple-400 transition shadow-lg shadow-indigo-500/30"
                >
                  Crear →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ✅ MODAL: crear campaña */}
        {createCampaignOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4"
            onClick={() => setCreateCampaignOpen(false)}
          >
            <div
              className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0c0c14] p-6 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-gray-400">Nueva campaña</div>
                  <div className="text-lg font-bold text-white mt-2">Subnivel dentro del proyecto</div>
                  <p className="text-sm text-gray-400 mt-2">Ej: “Meta Ads Semana 1”, “Stories”, “Banners Web”.</p>
                </div>

                <button
                  onClick={() => setCreateCampaignOpen(false)}
                  className="px-3 py-1.5 rounded-lg border border-white/10 hover:bg-white/10 transition text-xs text-gray-200"
                >
                  ✕
                </button>
              </div>

              {!allowCampaigns ? (
                <div className="mt-4 rounded-2xl border border-indigo-400/20 bg-indigo-500/10 p-4">
                  <div className="text-sm text-indigo-200 font-semibold">Campañas requieren Pro</div>
                  <p className="text-[12px] text-gray-300 mt-1">
                    Actualiza para organizar por campañas dentro del proyecto.
                  </p>
                  <button
                    onClick={() => goUpgrade("campaigns", "pro")}
                    className="mt-3 px-4 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-400 hover:to-purple-400 transition shadow-lg shadow-indigo-500/30"
                  >
                    Upgrade a Pro →
                  </button>
                </div>
              ) : (
                <>
                  <div className="mt-5">
                    <label className="block text-xs text-gray-400 mb-2">Nombre de la campaña</label>
                    <input
                      value={campaignName}
                      onChange={(e) => setCampaignName(e.target.value)}
                      placeholder="Ej: Meta Ads Semana 1"
                      className="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-gray-100 outline-none focus:ring-2 focus:ring-indigo-500/40 text-sm"
                    />
                  </div>

                  <div className="mt-5 flex justify-end gap-2">
                    <button
                      onClick={() => setCreateCampaignOpen(false)}
                      className="px-4 py-2 rounded-xl text-sm font-semibold border border-white/15 hover:bg-white/10 transition"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={createCampaign}
                      className="px-4 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-400 hover:to-purple-400 transition shadow-lg shadow-indigo-500/30"
                    >
                      Crear →
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ✅ MODAL: Preview */}
        {previewOpen && previewItem && (
          <div className="fixed inset-0 z-50 bg-black/80 px-4 py-6 overflow-y-auto" onClick={closePreview}>
            <div
              className="
                w-full max-w-5xl mx-auto
                rounded-2xl border border-white/10 bg-[#0c0c14] shadow-2xl overflow-hidden
                flex flex-col
                max-h-[90vh]
              "
              onClick={(e) => e.stopPropagation()}
            >
              {/* HEADER */}
              <div className="p-4 border-b border-white/10 flex items-start justify-between gap-4 shrink-0">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white truncate">{previewItem.prompt}</div>

                  <div className="mt-1 text-[11px] text-gray-400 flex flex-wrap gap-2 items-center">
                    <span className="px-2 py-1 rounded-full bg-black/40 border border-white/10">
                      {(previewItem.resolution || "—").toUpperCase()} · Export{" "}
                      {getExportSizeLabel((previewItem.resolution as ResolutionKey) || "720p")}
                    </span>
                    <span className="px-2 py-1 rounded-full bg-black/40 border border-white/10">
                      {prettyEngine(previewItem.engine)}
                    </span>
                    <span className="px-2 py-1 rounded-full bg-black/40 border border-white/10">
                      {previewItem.style}
                    </span>
                    <span className="px-2 py-1 rounded-full bg-black/40 border border-white/10">
                      {previewItem.costCredits ? `${previewItem.costCredits} créditos` : "—"}
                    </span>
                    <span className="text-gray-500">· {humanDate(previewItem.createdAt)}</span>
                  </div>

                  {needsUpscaleToBeReal((previewItem.resolution as ResolutionKey) || "720p") && (
                    <div className="mt-2 text-[11px] text-indigo-200">
                      Esta exportación incluye upscale automático (Real-ESRGAN) — haz zoom para notar nitidez en
                      texto/bordes.
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {previewItem.imageUrl && (
                    <a
                      href={previewItem.imageUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[11px] px-3 py-2 rounded-lg border border-white/10 hover:bg-white/10 transition text-gray-200"
                      title="Abrir imagen en nueva pestaña"
                    >
                      ↗ Abrir
                    </a>
                  )}
                  <button
                    onClick={closePreview}
                    className="text-[11px] px-3 py-2 rounded-lg border border-white/10 hover:bg-white/10 transition text-gray-200"
                  >
                    ✕ Cerrar
                  </button>
                </div>
              </div>

              {/* BODY SCROLL */}
              <div className="p-4 overflow-y-auto">
                <div className="rounded-2xl border border-white/10 bg-black/40 overflow-auto">
                  {previewItem.imageUrl ? (
                    <img
                      src={previewItem.imageUrl}
                      alt={previewItem.prompt}
                      className="w-full h-auto block"
                      draggable={false}
                    />
                  ) : (
                    <div className="h-80 bg-gradient-to-br from-indigo-800/40 via-purple-800/40 to-slate-900/60" />
                  )}
                </div>

                <div className="mt-3 text-[11px] text-gray-500">
                  Tip: para notar diferencia entre 1080p vs 4K/8K, abre la imagen en nueva pestaña y haz zoom al 200–300%.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

